import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { TabSymbol } from '@/features/Dashboard/context/EditChartApp';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import {
  initialForwardCurvesForm,
  initialSpotHistoryForm,
  initialTableForm
} from '@/features/Dashboard/form/AppForm/context/data';
import { useCurrentDashboardTabConfig } from '@/features/Dashboard/hooks/useCurrentDashboardConfig';
import { AppType } from '@/features/Dashboard/types';
import { convertTilesToActiveSymbols } from '@/features/Dashboard/utils/tileSymbolConverter';
import { SetState } from '@/types/helpers';

export enum DrawerStep {
  SelectApp = 'SelectApp',
  SelectMarketWidget = 'SelectMarketWidget',
  SelectSpotChart = 'SelectSpotChart',
  SelectFWDChart = 'SelectFWDChart',
  SelectBasicDataTable = 'SelectBasicDataTable',
  SelectHighlightedPrice = 'SelectHighlightedPrice'
}

interface DrawerStepsContextValue {
  drawerStep: DrawerStep | null;

  closeDrawer: () => void;
  setDrawerStep: SetState<DrawerStep | null>;
  isEditMode: boolean;
  setIsEditMode: SetState<boolean>;
  // When editing an existing item, holds its identifiers
  editingTarget: { appId?: string; tabId?: string; tileId?: string } | null;
  setEditingTarget: (target: { appId?: string; tabId?: string; tileId?: string } | null) => void;
  preventClose: boolean;
  setPreventClose: SetState<boolean>;
  confirmOpen: boolean;
  setConfirmOpen: SetState<boolean>;
  handleAttemptClose: () => void;
  handleConfirmClose: () => void;
  handleCancelClose: () => void;
  handleAttemptBack: () => void;
  setHasChanges: SetState<boolean>;
}

const DrawerStepsContext = createContext<DrawerStepsContextValue | undefined>(undefined);

export const useDrawerSteps = () => {
  const ctx = useContext(DrawerStepsContext);
  if (!ctx) throw new Error('useDrawerSteps must be used within DrawerStepsProvider');
  return ctx;
};

export const DrawerStepsProvider = ({ children }: { children: React.ReactNode }) => {
  const [drawerStep, setDrawerStep] = useState<DrawerStep | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTarget, setEditingTarget] = useState<{
    appId?: string;
    tabId?: string;
    tileId?: string;
  } | null>(null);
  const [preventClose, setPreventClose] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const close = useRef(false);

  const [hasChanges, setHasChanges] = useState(false);
  const { resetAppForm, setInitialValues, mode } = useAppForm();
  const { tiles: draftTiles } = useEditDashboardDraft();

  const closeDrawer = useCallback(() => {
    resetAppForm();
    setDrawerStep(null);
    setPreventClose(false);
    setIsEditMode(false);
    setEditingTarget(null);
    setHasChanges(false);
  }, [resetAppForm]);

  const handleAttemptClose = useCallback(() => {
    // Prevent closing the drawer on backdrop or ESC when blocked
    if (preventClose) return;
    close.current = true;
    // If there are unsaved changes, show confirm dialog
    if (hasChanges) {
      setConfirmOpen(true);
      return;
    }

    closeDrawer();
  }, [preventClose, hasChanges, closeDrawer]);

  const handleConfirmClose = useCallback(() => {
    setConfirmOpen(false);
    if (close.current) {
      // User intended to close the drawer
      // (backdrop/Escape/close button): always close
      closeDrawer();
    } else if (isEditMode) {
      closeDrawer();
      resetAppForm();
    } else {
      setDrawerStep(DrawerStep.SelectApp);
      resetAppForm();
    }
    setEditingTarget(null);
  }, [closeDrawer, isEditMode, resetAppForm]);

  const handleCancelClose = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const handleAttemptBack = useCallback(() => {
    if (preventClose) return;

    if (hasChanges) {
      close.current = false;
      setConfirmOpen(true);
      return;
    }

    if (isEditMode && editingTarget) {
      closeDrawer();
    } else {
      setDrawerStep(DrawerStep.SelectApp);
    }

    setIsEditMode(false);
    setEditingTarget(null);
    resetAppForm();
  }, [preventClose, hasChanges, isEditMode, editingTarget, resetAppForm, closeDrawer]);

  const dashboardConfig = useCurrentDashboardTabConfig();

  useEffect(() => {
    let tiles: TabSymbol[] = [];
    if (editingTarget && drawerStep !== DrawerStep.SelectHighlightedPrice) return;
    switch (drawerStep) {
      case DrawerStep.SelectMarketWidget:
      case DrawerStep.SelectApp:
        setInitialValues(null);
        break;
      case DrawerStep.SelectSpotChart:
        setInitialValues(initialSpotHistoryForm);
        break;
      case DrawerStep.SelectFWDChart:
        setInitialValues(initialForwardCurvesForm);
        break;
      case DrawerStep.SelectBasicDataTable:
        setInitialValues(initialTableForm);
        break;
      case DrawerStep.SelectHighlightedPrice:
        if (mode === 'create') {
          if (dashboardConfig?.[0] && 'type' in dashboardConfig[0]) {
            tiles = convertTilesToActiveSymbols(dashboardConfig[0].tiles);
          }
        } else {
          tiles = convertTilesToActiveSymbols(draftTiles);
        }

        setInitialValues({
          type: AppType.Tiles,
          symbols: { active: tiles, backlog: [] }
        });
        break;
      default:
        setInitialValues(null);
    }
  }, [drawerStep, mode, draftTiles, editingTarget, setInitialValues, dashboardConfig]);

  const value = useMemo(
    () => ({
      drawerStep,
      setDrawerStep,
      closeDrawer,
      setHasChanges,
      isEditMode,
      setIsEditMode,
      editingTarget,
      setEditingTarget,
      preventClose,
      setPreventClose,
      confirmOpen,
      setConfirmOpen,
      handleAttemptClose,
      handleConfirmClose,
      handleCancelClose,
      handleAttemptBack
    }),
    [
      drawerStep,
      closeDrawer,
      hasChanges,
      setHasChanges,
      isEditMode,
      editingTarget,
      preventClose,
      confirmOpen,
      handleAttemptClose,
      handleConfirmClose,
      handleCancelClose,
      handleAttemptBack
    ]
  );

  return <DrawerStepsContext.Provider value={value}>{children}</DrawerStepsContext.Provider>;
};
